import { useCallback, useEffect, useId, useRef, useState } from 'react';
import PrefetchLink from '@/components/shared/PrefetchLink';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavDropdownGroup {
  group: string;
  items: { name: string; href: string }[];
}

interface NavDropdownProps {
  label: string;
  groups: NavDropdownGroup[];
  /** Trigger className (matches surrounding nav items). */
  triggerClassName?: string;
  /** Whether to mark the trigger active (group has active route). */
  active?: boolean;
  /** Active-state per item, used to highlight current page in panel. */
  isItemActive?: (href: string) => boolean;
  /** Render dropdown panel in light mode (dark text on light background). */
  lightMode?: boolean;
}


const HOVER_CLOSE_DELAY = 150;

// Cross-dropdown coordination: when one NavDropdown opens, all others
// listen for this event and close themselves. Clicking a second trigger
// while another dropdown is open therefore swaps them in a single click.
const DROPDOWN_OPEN_EVENT = 'nav-dropdown-open';
let dropdownOpenSeq = 0;

/**
 * Suppress synthetic mouse events that follow a touch/pen interaction.
 * Hybrid devices (Surface, iPad+trackpad) report `(pointer: fine)` true
 * even when the user just tapped with a finger or stylus, so matchMedia
 * alone is not sufficient. We track the most recent pointerType and only
 * allow hover-to-open when it was a real mouse — within a short window
 * after a touch/pen event we ignore the synthesized mouseenter.
 */
const TOUCH_SUPPRESS_MS = 500;

/**
 * Accessible nav dropdown that unifies hover (desktop), click/tap (touch),
 * and full keyboard interaction (Enter/Space/Esc/ArrowUp/ArrowDown/Tab).
 *
 * Uses pointer media queries to distinguish hover-capable vs touch devices.
 */
const NavDropdown = ({
  label,
  groups,
  triggerClassName,
  active = false,
  isItemActive,
  lightMode = false,
}: NavDropdownProps) => {

  const [open, setOpen] = useState(false);
  const panelId = useId();
  const instanceIdRef = useRef<number>(0);
  if (instanceIdRef.current === 0) {
    dropdownOpenSeq += 1;
    instanceIdRef.current = dropdownOpenSeq;
  }

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const closeTimerRef = useRef<number | null>(null);
  // Last observed pointerType + when. Used to ignore the synthetic
  // mouseenter/mousemove that follows a touch tap or pen interaction.
  const lastPointerRef = useRef<{ type: string; at: number }>({
    type: 'mouse',
    at: 0,
  });

  const flatItems = groups.flatMap((g) => g.items);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY);
  }, [cancelClose]);

  const closeImmediate = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);

  // When another dropdown announces it's opening, close this one.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      if (detail !== instanceIdRef.current) {
        cancelClose();
        setOpen(false);
      }
    };
    window.addEventListener(DROPDOWN_OPEN_EVENT, handler as EventListener);
    return () => window.removeEventListener(DROPDOWN_OPEN_EVENT, handler as EventListener);
  }, [cancelClose]);

  // Announce open state changes so peers can close.
  useEffect(() => {
    if (open) {
      window.dispatchEvent(
        new CustomEvent(DROPDOWN_OPEN_EVENT, { detail: instanceIdRef.current }),
      );
    }
  }, [open]);

  /**
   * True only when (a) the device exposes a fine pointer AND (b) the most
   * recent pointer event came from an actual mouse (or no pointer event has
   * been observed yet, which only happens before any interaction).
   *
   * On touch/pen taps the browser fires a synthesized mouseenter shortly
   * after pointerdown; that is exactly the case we want to ignore.
   */
  const isHoverIntent = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    if (!window.matchMedia?.('(pointer: fine)').matches) return false;
    const { type, at } = lastPointerRef.current;
    if (type === 'mouse') return true;
    // Within the suppression window, treat as touch/pen — ignore.
    return Date.now() - at > TOUCH_SUPPRESS_MS;
  }, []);

  // Record pointerType on every pointer interaction so the subsequent
  // synthesized mouseenter can be filtered. We register both via React
  // (onPointerDown / onPointerMove on the wrapper, see below) AND via a
  // window-level capture listener so taps anywhere on the page reset the
  // window of suspicion before any later mouseenter on the dropdown.
  const recordPointer = useCallback((type: string | undefined) => {
    lastPointerRef.current = { type: type || 'mouse', at: Date.now() };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPointer = (e: PointerEvent) => recordPointer(e.pointerType);
    window.addEventListener('pointerdown', onPointer, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', onPointer, { capture: true });
    };
  }, [recordPointer]);

  // Outside-click close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      closeImmediate();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open, closeImmediate]);

  // Cleanup any pending close timer on unmount
  useEffect(() => () => cancelClose(), [cancelClose]);

  const focusItem = (index: number) => {
    const total = flatItems.length;
    if (total === 0) return;
    const wrapped = ((index % total) + total) % total;
    itemRefs.current[wrapped]?.focus();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setOpen((prev) => !prev);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setOpen(true);
        // Defer focus until panel renders
        requestAnimationFrame(() => focusItem(0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => focusItem(flatItems.length - 1));
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          closeImmediate();
        }
        break;
    }
  };

  const handlePanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const activeEl = document.activeElement as HTMLElement | null;
    const currentIndex = itemRefs.current.findIndex((el) => el === activeEl);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem(currentIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem(currentIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(flatItems.length - 1);
        break;
      case 'Escape':
        e.preventDefault();
        closeImmediate();
        triggerRef.current?.focus();
        break;
      case 'Tab':
        // Let Tab close the menu naturally and move focus forward.
        closeImmediate();
        break;
    }
  };

  let itemIndex = -1;

  return (
    <div
      className="relative"
      onPointerDown={(e) => recordPointer(e.pointerType)}
      onPointerMove={(e) => recordPointer(e.pointerType)}
      onMouseEnter={() => {
        // Only honor mouseenter when the latest pointer was a real mouse.
        // This prevents accidental open from the synthesized mouseenter
        // that fires after a touch/stylus tap on hybrid devices.
        if (isHoverIntent()) {
          cancelClose();
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        // Always schedule the delayed close on real-mouse leave so visitors
        // can briefly cross the trigger↔panel gap without the menu collapsing.
        if (isHoverIntent()) {
          scheduleClose();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        onMouseLeave={() => {
          // Belt-and-braces: also schedule the same delayed close when the
          // pointer leaves the trigger directly. Using the same scheduler
          // means cancelClose() on the panel mouseenter cleanly preempts it.
          if (isHoverIntent()) {
            scheduleClose();
          }
        }}
        onFocus={() => {
          // Open on keyboard focus only with real-mouse hover capability.
          // Touch/pen taps that incidentally focus the button must not
          // auto-expand the menu (the user has to tap again or press Enter).
          if (isHoverIntent()) {
            cancelClose();
            setOpen(true);
          }
        }}
        className={triggerClassName}
        data-active={active || undefined}
      >
        {label}
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform duration-300',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="menu"
          aria-label={label}
          onKeyDown={handlePanelKeyDown}
          onMouseEnter={cancelClose}
          onMouseLeave={() => {
            // Same delayed close on panel exit. Re-entering the trigger or
            // panel within HOVER_CLOSE_DELAY cancels via cancelClose().
            if (isHoverIntent()) {
              scheduleClose();
            }
          }}
          className={cn(
            'absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 min-w-[220px] p-2 shadow-lg border',
            lightMode
              ? 'bg-background border-border'
              : 'bg-hero border-hero-text/20',
          )}
        >
          {groups.map((group, groupIndex) => (
            <div
              key={group.group}
              className={cn(
                groupIndex > 0 && (lightMode ? 'mt-3 pt-3 border-t border-border' : 'mt-3 pt-3 border-t border-hero-text/10'),
              )}
            >
              <div className={cn(
                'px-3 py-1.5 text-[10px] tracking-widest uppercase font-sans',
                lightMode ? 'text-muted-foreground' : 'text-hero-text/40',
              )}>
                {group.group}
              </div>
              {group.items.map((item) => {
                itemIndex += 1;
                const idx = itemIndex;
                const isCurrent = isItemActive?.(item.href) ?? false;
                return (
                  <PrefetchLink
                    key={item.name}
                    to={item.href}
                    role="menuitem"
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    onClick={closeImmediate}
                    className={cn(
                      'flex items-center w-full px-3 py-2 cursor-pointer transition-all duration-200 rounded-sm',
                      lightMode
                        ? 'hover:translate-x-1 hover:bg-secondary/50 focus:bg-secondary/50'
                        : 'hover:translate-x-1 hover:bg-hero-text/10 focus:bg-hero-text/10',
                      'focus:outline-none focus:translate-x-1',
                      isCurrent
                        ? (lightMode ? 'text-foreground bg-secondary/50' : 'text-hero-text bg-hero-text/10')
                        : (lightMode ? 'text-muted-foreground hover:text-foreground' : 'text-hero-text/70 hover:text-hero-text'),
                    )}
                  >

                    <span className="font-sans text-xs tracking-wide">
                      {item.name}
                    </span>
                  </PrefetchLink>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NavDropdown;